/**
 * Standalone Browser Stream Viewer Page
 * Opens in a separate window/tab like Google Meet
 * 
 * Copyright 2025 Mia srl
 */

import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import config from '../config';
import './BrowserStreamViewer.css';

/**
 * Adaptive Stream Manager
 * Detects network quality and requests appropriate streaming quality
 */
class AdaptiveStreamManager {
  constructor() {
    this.networkQuality = 'medium'; // high/medium/low
    this.lastLatencies = [];
    this.maxLatencySamples = 10;
  }

  detectNetworkQuality() {
    // Use Network Information API if available
    if ('connection' in navigator) {
      const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      
      if (connection && connection.effectiveType) {
        // Map effective types to quality levels
        const typeMap = {
          'slow-2g': 'low',
          '2g': 'low',
          '3g': 'medium',
          '4g': 'high'
        };
        
        this.networkQuality = typeMap[connection.effectiveType] || 'medium';
        return this.networkQuality;
      }
      
      // Fallback to downlink speed if available
      if (connection && connection.downlink) {
        if (connection.downlink < 1) {
          this.networkQuality = 'low';
        } else if (connection.downlink < 5) {
          this.networkQuality = 'medium';
        } else {
          this.networkQuality = 'high';
        }
        return this.networkQuality;
      }
    }

    // Fallback: Use latency-based detection
    if (this.lastLatencies.length >= 3) {
      const avgLatency = this.lastLatencies.reduce((a, b) => a + b, 0) / this.lastLatencies.length;
      
      if (avgLatency > 500) {
        this.networkQuality = 'low';
      } else if (avgLatency > 200) {
        this.networkQuality = 'medium';
      } else {
        this.networkQuality = 'high';
      }
    }

    return this.networkQuality;
  }

  recordLatency(latency) {
    this.lastLatencies.push(latency);
    if (this.lastLatencies.length > this.maxLatencySamples) {
      this.lastLatencies.shift();
    }
  }

  getQuality() {
    return this.networkQuality;
  }
}

/**
 * Standalone Browser Stream Viewer Component
 * Designed to run in a separate window
 */
const BrowserStreamViewerStandalone = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('sessionId');
  const initialQuality = searchParams.get('quality') || 'medium';

  const [isConnected, setIsConnected] = useState(false);
  const [currentQuality, setCurrentQuality] = useState(initialQuality);
  const [frameCount, setFrameCount] = useState(0);
  const [lastFrameTime, setLastFrameTime] = useState(Date.now());
  const [fps, setFps] = useState(0);
  const [error, setError] = useState(null);
  const [alwaysOnTop, setAlwaysOnTop] = useState(true);
  
  const wsRef = useRef(null);
  const imgRef = useRef(null);
  const streamManagerRef = useRef(new AdaptiveStreamManager());
  const frameTimeoutRef = useRef(null);
  const fpsIntervalRef = useRef(null);

  useEffect(() => {
    if (!sessionId) {
      setError('No session ID provided');
      return;
    }

    // Update document title
    document.title = `Browser Stream - ${sessionId.slice(0, 8)}`;

    // Handle always-on-top functionality
    const focusIntervalId = alwaysOnTop ? setInterval(() => {
      if (!document.hidden) {
        window.focus();
      }
    }, 1000) : null;

    connectWebSocket();

    // Calculate FPS every second
    fpsIntervalRef.current = setInterval(() => {
      const now = Date.now();
      const timeDiff = (now - lastFrameTime) / 1000;
      if (timeDiff > 0) {
        setFps(Math.round(frameCount / timeDiff));
      }
    }, 1000);

    return () => {
      // Cleanup focus interval
      if (focusIntervalId) clearInterval(focusIntervalId);
      
      // Cleanup WebSocket
      disconnectWebSocket();
      
      // Cleanup FPS interval
      if (fpsIntervalRef.current) {
        clearInterval(fpsIntervalRef.current);
      }
      
      // Cleanup frame timeout
      if (frameTimeoutRef.current) {
        clearTimeout(frameTimeoutRef.current);
      }
    };
  }, [sessionId, alwaysOnTop]);

  const connectWebSocket = () => {
    try {
      // Convert HTTP URL to WebSocket URL
      let wsBaseUrl = config.bffUrl
        .replace('http://', 'ws://')
        .replace('https://', 'wss://');
      
      // Remove /api prefix if present (WebSocket routes are at root level)
      wsBaseUrl = wsBaseUrl.replace(/\/api$/, '');

      // Detect initial quality
      const detectedQuality = streamManagerRef.current.detectNetworkQuality();
      const quality = detectedQuality || currentQuality;
      
      const url = `${wsBaseUrl}/ws/browser/${sessionId}?quality=${quality}`;
      
      console.log(`🌐 Connecting to browser stream: ${url}`);

      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('✅ Browser stream connected');
        setIsConnected(true);
        setError(null);
        setCurrentQuality(quality);
      };

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          
          if (message.type === 'frame') {
            // Update image with new frame
            if (imgRef.current && message.data) {
              const frameReceiveTime = Date.now();
              const latency = frameReceiveTime - message.timestamp;
              
              // Record latency for adaptive quality
              streamManagerRef.current.recordLatency(latency);
              
              imgRef.current.src = `data:image/jpeg;base64,${message.data}`;
              setFrameCount(prev => prev + 1);
              
              // Reset frame timeout
              if (frameTimeoutRef.current) {
                clearTimeout(frameTimeoutRef.current);
              }
              
              // Detect stream stall (no frames for 5 seconds)
              frameTimeoutRef.current = setTimeout(() => {
                setError('Stream stalled - no frames received');
              }, 5000);
            }
          } else if (message.type === 'connected') {
            console.log('Browser stream ready');
          }
        } catch (err) {
          console.error('Error handling WebSocket message:', err);
        }
      };

      ws.onerror = (error) => {
        console.error('❌ WebSocket error:', error);
        setError('WebSocket connection error');
      };

      ws.onclose = (event) => {
        console.log('WebSocket closed:', event.code, event.reason);
        setIsConnected(false);
        
        if (event.code !== 1000) {
          setError(`Connection closed: ${event.reason || 'Unknown error'}`);
        }
      };

    } catch (err) {
      console.error('Failed to connect WebSocket:', err);
      setError(`Connection failed: ${err.message}`);
    }
  };

  const disconnectWebSocket = () => {
    if (wsRef.current) {
      wsRef.current.close(1000, 'User closed viewer');
      wsRef.current = null;
    }
  };

  const changeQuality = (newQuality) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      // Send quality change message
      wsRef.current.send(JSON.stringify({
        type: 'changeQuality',
        quality: newQuality
      }));
      
      setCurrentQuality(newQuality);
      console.log(`Quality changed to: ${newQuality}`);
    }
  };

  const handleAutoQuality = () => {
    const detectedQuality = streamManagerRef.current.detectNetworkQuality();
    changeQuality(detectedQuality);
  };

  const closeWindow = () => {
    window.close();
  };

  if (!sessionId) {
    return (
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        height: '100vh', 
        background: '#1a1a1a',
        color: '#fff',
        fontFamily: 'system-ui, -apple-system, sans-serif'
      }}>
        <div style={{ textAlign: 'center' }}>
          <h2>⚠️ Error</h2>
          <p>No browser session ID provided</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ 
      display: 'flex', 
      flexDirection: 'column', 
      height: '100vh', 
      background: '#1a1a1a',
      overflow: 'hidden'
    }}>
      <div className="browser-stream-viewer" style={{ 
        margin: 0, 
        maxWidth: '100%', 
        height: '100%',
        borderRadius: 0
      }}>
        <div className="browser-stream-header">
          <div className="stream-info">
            <span className={`status-indicator ${isConnected ? 'connected' : 'disconnected'}`}>
              {isConnected ? '● Live' : '○ Disconnected'}
            </span>
            <span className="fps-counter">{fps} FPS</span>
            <span className="frame-counter">{frameCount} frames</span>
          </div>
          
          <div className="stream-controls">
            <select 
              value={currentQuality} 
              onChange={(e) => changeQuality(e.target.value)}
              className="quality-selector"
              disabled={!isConnected}
            >
              <option value="high">High Quality</option>
              <option value="medium">Medium Quality</option>
              <option value="low">Low Quality</option>
            </select>
            
            <button 
              onClick={handleAutoQuality}
              className="auto-quality-btn"
              disabled={!isConnected}
              title="Detect and set optimal quality"
            >
              Auto
            </button>
            
            <label 
              className="always-on-top-toggle"
              title="Keep window on top"
              style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '5px',
                cursor: 'pointer',
                fontSize: '12px',
                color: '#fff'
              }}
            >
              <input 
                type="checkbox" 
                checked={alwaysOnTop}
                onChange={(e) => setAlwaysOnTop(e.target.checked)}
              />
              📌 Pin
            </label>
            
            <button 
              onClick={closeWindow}
              className="close-btn"
              title="Close window"
            >
              ✕
            </button>
          </div>
        </div>

        <div className="browser-stream-content" style={{ flex: 1 }}>
          {error && (
            <div className="stream-error">
              <span>⚠️ {error}</span>
              <button onClick={connectWebSocket}>Retry</button>
            </div>
          )}
          
          {!isConnected && !error && (
            <div className="stream-loading">
              <div className="loading-spinner"></div>
              <p>Connecting to browser stream...</p>
            </div>
          )}
          
          <img 
            ref={imgRef}
            className="browser-frame"
            alt="Browser view"
            style={{ 
              display: isConnected ? 'block' : 'none',
              width: '100%',
              height: '100%',
              objectFit: 'contain'
            }}
          />
        </div>
        
        <div className="browser-stream-footer">
          <div className="session-info">
            <span className="session-id" title={sessionId}>
              Session: {sessionId.slice(0, 8)}...
            </span>
            <span className="quality-badge">{currentQuality}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BrowserStreamViewerStandalone;
